import { Injectable } from '@angular/core';
import { HubConnection, HubConnectionBuilder, HubConnectionState, LogLevel } from '@microsoft/signalr';
import { BehaviorSubject } from 'rxjs';
import { environment } from '../../environments/environment';

type SignalREventHandler = (...args: unknown[]) => void;

export type { SignalREventHandler };

@Injectable({
  providedIn: 'root'
})
export class SignalRService {
  private connection: HubConnection | null = null;
  private readonly handlers = new Map<string, Set<SignalREventHandler>>();
  private startPromise: Promise<void> | null = null;

  private connectionStateSubject = new BehaviorSubject<HubConnectionState>(HubConnectionState.Disconnected);
  readonly connectionState$ = this.connectionStateSubject.asObservable();

  get isConnected(): boolean {
    return this.connection?.state === HubConnectionState.Connected;
  }

  get connectionId(): string | null {
    return this.connection?.connectionId ?? null;
  }

  get connectionState(): HubConnectionState {
    return this.connection?.state ?? HubConnectionState.Disconnected;
  }

  private getHubUrl(): string {
    if (environment.notificationHubUrl) {
      return environment.notificationHubUrl;
    }

    const apiBase = environment.apiBaseUrl.replace(/\/$/, '');
    const origin = apiBase.replace(/\/api\/?$/, '');
    return `${origin}/notificationHub`;
  }

  private ensureConnection(): HubConnection {
    if (!this.connection) {
      const hubUrl = this.getHubUrl();
      console.log('[SignalR] Building hub connection', { hubUrl, withCredentials: true });

      this.connection = new HubConnectionBuilder()
        .withUrl(hubUrl, { withCredentials: true })
        .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
        .configureLogging(LogLevel.Information)
        .build();

      this.connection.onreconnecting((error) => {
        console.warn('[SignalR] Reconnecting...', error ?? '');
        this.connectionStateSubject.next(HubConnectionState.Reconnecting);
      });

      this.connection.onreconnected((connectionId) => {
        console.log('[SignalR] Reconnected', { connectionId, state: this.connection?.state });
        this.connectionStateSubject.next(HubConnectionState.Connected);
      });

      this.connection.onclose((error) => {
        console.warn('[SignalR] Connection closed', error ?? 'clean close');
        this.connectionStateSubject.next(HubConnectionState.Disconnected);
        this.startPromise = null;
      });
    }

    return this.connection;
  }

  private reregisterHandlers(): void {
    if (!this.connection) {
      return;
    }

    console.log('[SignalR] Re-registering handlers', {
      events: Array.from(this.handlers.keys()),
      counts: Array.from(this.handlers.entries()).map(([name, set]) => ({ name, count: set.size }))
    });

    this.handlers.forEach((eventHandlers, eventName) => {
      eventHandlers.forEach(handler => {
        this.connection!.on(eventName, handler);
      });
    });
  }

  on(eventName: string, handler: SignalREventHandler): void {
    const eventHandlers = this.handlers.get(eventName) ?? new Set<SignalREventHandler>();
    eventHandlers.add(handler);
    this.handlers.set(eventName, eventHandlers);

    console.log('[SignalR] Handler registered', {
      eventName,
      handlerCount: eventHandlers.size,
      hasConnection: !!this.connection,
      state: this.connection?.state ?? 'none'
    });

    if (this.connection) {
      this.connection.on(eventName, handler);
    }
  }

  off(eventName: string, handler?: SignalREventHandler): void {
    console.log('[SignalR] Handler removed', { eventName, hasSpecificHandler: !!handler });

    if (handler) {
      const eventHandlers = this.handlers.get(eventName);
      eventHandlers?.delete(handler);
      if (eventHandlers?.size === 0) {
        this.handlers.delete(eventName);
      }
      this.connection?.off(eventName, handler);
      return;
    }

    this.handlers.delete(eventName);
    this.connection?.off(eventName);
  }

  async start(): Promise<void> {
    const connection = this.ensureConnection();
    const hubUrl = this.getHubUrl();

    console.log('[SignalR] start() called', {
      hubUrl,
      state: connection.state,
      connectionId: connection.connectionId,
      registeredEvents: Array.from(this.handlers.keys())
    });

    if (connection.state === HubConnectionState.Connected) {
      console.log('[SignalR] Already connected — skipping start');
      return;
    }

    if (this.startPromise) {
      console.log('[SignalR] Start already in progress — awaiting existing promise');
      return this.startPromise;
    }

    this.reregisterHandlers();

    this.startPromise = connection.start()
      .then(() => {
        this.connectionStateSubject.next(HubConnectionState.Connected);
        console.log('[SignalR] Connected successfully', {
          hubUrl,
          state: connection.state,
          connectionId: connection.connectionId,
          registeredEvents: Array.from(this.handlers.keys())
        });
      })
      .catch((error) => {
        this.startPromise = null;
        console.error('[SignalR] Connection FAILED', {
          hubUrl,
          errorName: error?.name,
          errorMessage: error?.message,
          error
        });
        throw error;
      });

    return this.startPromise;
  }

  async invoke<T = unknown>(methodName: string, ...args: unknown[]): Promise<T> {
    const connection = this.ensureConnection();

    console.log('[SignalR] invoke()', { methodName, argCount: args.length, state: connection.state });

    if (connection.state !== HubConnectionState.Connected) {
      await this.start();
    }

    try {
      const result = await connection.invoke<T>(methodName, ...args);
      console.log('[SignalR] invoke() success', { methodName });
      return result;
    } catch (error) {
      console.error('[SignalR] invoke() FAILED', { methodName, error });
      throw error;
    }
  }

  async stop(): Promise<void> {
    if (!this.connection) {
      console.log('[SignalR] stop() — no connection');
      return;
    }

    console.log('[SignalR] stop() called', {
      state: this.connection.state,
      connectionId: this.connection.connectionId
    });

    this.startPromise = null;

    if (this.connection.state === HubConnectionState.Disconnected) {
      return;
    }

    try {
      await this.connection.stop();
      console.log('[SignalR] Disconnected successfully');
    } catch (error) {
      console.error('[SignalR] Disconnect FAILED', error);
    } finally {
      this.connectionStateSubject.next(HubConnectionState.Disconnected);
    }
  }

  reset(): void {
    console.log('[SignalR] reset() — clearing handlers and connection');
    this.handlers.clear();
    this.connection = null;
    this.startPromise = null;
    this.connectionStateSubject.next(HubConnectionState.Disconnected);
  }
}
